import { supabase } from '../supabaseClient'
import { AgeBand, BookableSchedule } from '../containers/BookableActivityPage/interfaces'
import { defaultFormatDate } from '../utils/format'

class BookableService {
  createBookableActivityTransaction = async (
    bookableData: any,
    activityData: any,
    schedulesData: any[],
    ageBandsData: any[],
  ): Promise<any> => {
    try {
      const formattedSchedules = schedulesData.map((schedule) => ({
        ...schedule,
        start_date: schedule.start_date ? defaultFormatDate(new Date(schedule.start_date)) : null,
        earliest_start_time: schedule.earliest_start_time
          ? defaultFormatDate(new Date(schedule.earliest_start_time))
          : null,
        latest_start_time: schedule.latest_start_time
          ? defaultFormatDate(new Date(schedule.latest_start_time))
          : null,
      }))

      const { data, error } = await supabase.rpc('create_bookable_and_bookable_activity', {
        bookable_data: bookableData,
        activity_data: activityData,
        schedules_data: formattedSchedules,
        age_bands_data: ageBandsData,
      })

      if (error) {
        console.error('Error creating bookable and activity:', error)
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error({ error })
      throw error
    }
  }

  createBookable = async (payload: any): Promise<any> => {
    try {
      let { error: insertError, data } = await supabase.from('bookables').insert(payload)

      if (insertError) {
        console.error(insertError)
        throw insertError
      }
      return data
    } catch (error) {
      console.error({ error })
      throw error
    }
  }

  deleteBookable = async (id: string, deletedBy: string): Promise<any> => {
    try {
      const deletedOn = new Date().toISOString()
      const { error } = await supabase
        .from('bookables')
        .update({
          deleted: true,
          deleted_on: deletedOn,
          deleted_by: deletedBy,
        })
        .eq('id', id)

      if (error) {
        console.error(error)
      }

      return true
    } catch (error) {
      console.error({ error })
    }
  }

  countBookables = async (
    term?: string,
    supplierUuid?: string,
    filterDays?: number,
  ): Promise<number> => {
    try {
      let query = supabase
        .from('bookables')
        .select(
          `
            *,
            bookings!inner(start_time) 
          `,
          { count: 'exact' },
        )
        .eq('deleted', false)

      if (term && term.trim().length > 0) {
        query = query.ilike('name', `%${term}%`)
      }

      if (supplierUuid) {
        query = query.eq('supplier_uuid', supplierUuid)
      }

      if (filterDays) {
        const fromDate = new Date().toISOString()
        const toDate = new Date()
        toDate.setDate(toDate.getDate() + filterDays)

        query = query
          .gte('bookings.start_time', fromDate)
          .lte('bookings.start_time', toDate.toISOString())
      }

      const { count, error } = await query
      if (error) {
        console.error('Error fetching total count:', error)
        return 0
      }

      return count ?? 0
    } catch (e) {
      console.error(e)
      return 0
    }
  }

  getPaginatedBookables = async (
    page: number,
    term?: string,
    supplierUuid?: string,
    sortOptions?: { sortField: string | null; sortDirection: 'asc' | 'desc' },
    itemsPerPage: number = 10,
    filterDays?: number,
  ): Promise<{ data: any[]; count: number }> => {
    try {
      const count = await this.countBookables(term, supplierUuid, filterDays)

      let query = supabase
        .from('bookables')
        .select(
          `
            *,
            bookings!inner(start_time) 
          `,
          { count: 'exact' },
        )
        .eq('deleted', false)

      if (term && term.trim().length > 0) {
        query = query.ilike('name', `%${term}%`)
      }

      if (supplierUuid) {
        query = query.eq('supplier_uuid', supplierUuid)
      }

      if (sortOptions?.sortField && sortOptions.sortDirection) {
        query = query.order(sortOptions.sortField, {
          ascending: sortOptions.sortDirection === 'asc',
        })
      }

      if (filterDays) {
        const fromDate = new Date().toISOString()
        const toDate = new Date()
        toDate.setDate(toDate.getDate() + filterDays)

        query = query
          .gte('bookings.start_time', fromDate)
          .lte('bookings.start_time', toDate.toISOString())
      }

      const start = (page - 1) * (itemsPerPage ?? 5)
      const end = page * (itemsPerPage ?? 5) - 1
      query = query.range(start, end)

      const { data, error } = await query
      if (error) {
        console.error(error)
        throw error
      }

      return {
        data,
        count,
      }
    } catch (error) {
      console.error({ error })
      throw error
    }
  }

  createAgeBands = async (ageBands: AgeBand[]): Promise<void> => {
    const normalizedBands = Array.isArray(ageBands) ? ageBands : [ageBands]

    try {
      const { error } = await supabase
        .from('age_bands')
        .upsert(normalizedBands, { onConflict: 'bookable_uuid,age_band_type' })

      if (error) {
        console.error('Error creating/updating age band:', error)
        throw error
      }
    } catch (error) {
      console.error('Error:', error)
      throw error
    }
  }

  updateAgeBand = async (id: number, payload: Partial<AgeBand>): Promise<void> => {
    const { error } = await supabase.from('age_bands').update(payload).eq('id', id)
    if (error) {
      console.error('Error updating age band:', error)
      throw error
    }
  }

  deleteAgeBandsByBookableUUID = async (bookableUUID: string): Promise<void> => {
    try {
      const { error } = await supabase.from('age_bands').delete().eq('bookable_uuid', bookableUUID)

      if (error) {
        console.error('Error deleting age bands:', error)
        throw error
      }
    } catch (error) {
      console.error('Error:', error)
      throw error
    }
  }

  createBookableSchedules = async (schedules: BookableSchedule[]): Promise<void> => {
    try {
      for (const schedule of schedules) {
        const { error } = await supabase.from('bookable_schedules').insert(schedule)
        if (error) {
          console.error('Error creating bookable schedule:', error)
          throw error
        }
      }
    } catch (error) {
      console.error('Error:', error)
      throw error
    }
  }

  updateBookableSchedule = async (
    id: number,
    payload: Partial<BookableSchedule>,
  ): Promise<void> => {
    const { error } = await supabase.from('bookable_schedules').update(payload).eq('id', id)
    if (error) {
      console.error('Error updating schedule:', error)
      throw error
    }
  }

  getBookableById = async (id: string): Promise<any> => {
    try {
      const { data, error } = await supabase.from('bookables').select('*').eq('id', id).single()
      if (error) throw error
      return { data }
    } catch (error) {
      console.error('Error fetching bookable by ID:', error)
      throw error
    }
  }

  getBookableActivityByUUID = async (uuid: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('bookable_activities')
        .select('*')
        .eq('bookable', uuid)
      if (error) throw error
      return { data }
    } catch (error) {
      console.error('Error fetching bookable Activity by UUID:', error)
      throw error
    }
  }

  updateBookable = async (uuid: string, payload: any): Promise<any> => {
    try {
      const { data, error } = await supabase.from('bookables').update(payload).eq('uuid', uuid)
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating bookable:', error)
      throw error
    }
  }

  getAgeBandsByBookableId = async (bookableId: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('age_bands')
        .select('*')
        .eq('bookable_uuid', bookableId)

      if (error) throw error
      return { data }
    } catch (error) {
      console.error('Error fetching age bands:', error)
      throw error
    }
  }

  getSchedulesByBookableUUID = async (bookableUuid: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('bookable_schedules')
        .select('*')
        .eq('bookable_uuid', bookableUuid)

      if (error) throw error
      return { data }
    } catch (error) {
      console.error('Error fetching schedules:', error)
      throw error
    }
  }
}

const bookableService = new BookableService()

export default bookableService
